import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { CircularProgress } from 'src/components/CircularProgress';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { checkAppointmentEligibility } from 'src/models/appointment/appointment';
import { Service } from 'src/models/service/_types';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import { Identifier } from 'src/types/utilityTypes';

import { VisitUserInfo } from './VisitUserInfo';
import { PendingVisit } from './_types';

interface PendingVisitRowProps {
  pending: PendingVisit;
  appointmentId: Identifier;
  service?: Partial<Service> | null;
  onSave: () => void;
  onSaveToAllRecurring?: () => void;
  onRemove: () => void;
  isRecurring?: boolean;
}

/**
 * PendingVisitRow - Display for a visit that hasn't been saved yet
 *
 * Compact layout matching VisitRow styling:
 * - Dashed border to indicate pending state
 * - User info with NEW badge
 * - Single-click action chips (no dropdown menus)
 */
export const PendingVisitRow: FC<PendingVisitRowProps> = ({
  pending,
  appointmentId,
  service,
  onSave,
  onSaveToAllRecurring,
  onRemove,
  isRecurring = false,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.viewEventDialog.pendingVisitRow' });
  const { isSaving } = pending;
  const { canBookVisitWithoutPackage } = usePermissionCheck();

  // Fetch eligibility
  const { data: eligibility, isLoading: isCheckingEligibility } = useQuery({
    queryKey: ['appointmentEligibility', appointmentId, pending.userId],
    queryFn: () =>
      checkAppointmentEligibility({
        appointment: { id: appointmentId } as any,
        user: { id: pending.userId! } as any,
      }),
    enabled: !!pending.userId && !!appointmentId,
  });

  const hasInsufficientFunds =
    eligibility?.trainerReason === 'insufficient_funds';
  const cannotBookWithoutPackage = service?.id
    ? !canBookVisitWithoutPackage(service as Service)
    : false;
  const shouldBlockSave =
    cannotBookWithoutPackage && (hasInsufficientFunds || isCheckingEligibility);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: 2,
        borderColor: 'warning.main',
        borderStyle: 'dashed',
      }}
    >
      {/* Row 1: User info + NEW badge */}
      <Box sx={{ mb: 1 }}>
        <VisitUserInfo
          userName={pending.userName}
          userId={pending.userId}
          clientId={pending.client?.clientId?.toString()}
          appointmentId={appointmentId}
          tagString={(pending.client as any)?.tagString}
          isNew
        />
      </Box>

      {/* Row 2: Actions as single-click chips */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          pt: 1,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {isSaving ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size="small" />
            <Typography variant="caption" color="text.secondary">
              {t('saving')}
            </Typography>
          </Stack>
        ) : shouldBlockSave ? (
          <>
            <Typography variant="caption" color="error.main" sx={{ flex: 1 }}>
              {t('cannotSaveInsufficientBalance')}
            </Typography>
            <Chip
              label={t('remove')}
              size="small"
              color="error"
              variant="outlined"
              onClick={onRemove}
              clickable
            />
          </>
        ) : (
          <>
            <Chip
              label={t('addToSession')}
              size="small"
              color="success"
              variant="outlined"
              onClick={onSave}
              clickable
            />
            {isRecurring && (
              <Chip
                label={t('addToSessionAndMakeRecurring')}
                size="small"
                color="primary"
                variant="outlined"
                onClick={onSaveToAllRecurring}
                clickable
              />
            )}
            <Chip
              label={t('remove')}
              size="small"
              color="error"
              variant="outlined"
              onClick={onRemove}
              clickable
            />
          </>
        )}
      </Box>
    </Box>
  );
};
